// Модуль трекинга поведения пользователей для eventos-front.
// События уходят батчами на POST /api/v0/events (см. server EventsApiImpl).
// Поля в camelCase — совпадают со схемой TrackingEventData в spec.yml.

const ORIGINATOR = 'eventos-front';
const ANON_ID_KEY = 'eventos_anon_id';
const ENDPOINT = '/api/v0/events';
const FLUSH_DELAY_MS = 100;

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface EventContext {
  browser: string;
  os: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  language: string;
  timezone: string;
}

export interface TrackingEvent {
  originator: string;
  anonId: string;
  sessionId: string;
  userId?: number;
  accountId?: number;
  timestamp: string;
  event: string;
  props: Record<string, unknown>;
  utm?: UTMParams;
  context?: EventContext;
}

declare global {
  interface Window {
    __eventos_session_id?: string;
    ym?: (counterId: number, method: string, callback?: (clientId: string) => void) => void;
  }
}

// Счётчик Яндекс.Метрики eventos (одинаковый у front и site).
const YM_COUNTER_ID = 62529322;

// --- identity ------------------------------------------------------------

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getAnonId(): string {
  if (typeof window === 'undefined') return '';
  let anonId = localStorage.getItem(ANON_ID_KEY);
  if (!anonId) {
    anonId = generateUUID();
    localStorage.setItem(ANON_ID_KEY, anonId);
  }
  return anonId;
}

export function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  if (!window.__eventos_session_id) {
    window.__eventos_session_id = generateUUID();
  }
  return window.__eventos_session_id;
}

// Токен сессии для авторизации ingest-запроса: если событие несёт userId, сервер
// проверяет его против Bearer-токена. Берём тот же sessionId, что и API-клиенты.
function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('sessionId');
}

// Текущий пользователь — выставляется приложением после логина/проверки сессии.
let currentUserId: number | undefined;
let currentAccountId: number | undefined;

export function setTrackingUser(userId?: number | string, accountId?: number): void {
  if (userId === undefined || userId === null || userId === '') {
    currentUserId = undefined;
  } else {
    const n = typeof userId === 'number' ? userId : parseInt(userId, 10);
    currentUserId = isNaN(n) ? undefined : n;
  }
  currentAccountId = accountId ?? undefined;
}

// --- context / utm -------------------------------------------------------

function parseUTMParams(): UTMParams | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
  const utm: UTMParams = {};
  let has = false;
  for (const k of keys) {
    const v = params.get(k);
    if (v) {
      utm[k] = v;
      has = true;
    }
  }
  return has ? utm : undefined;
}

function detectBrowser(ua: string): string {
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('OPR') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'unknown';
}

function detectOS(ua: string): string {
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS')) return 'macOS';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iOS')) return 'iOS';
  if (ua.includes('Linux')) return 'Linux';
  return 'unknown';
}

function detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w <= 1024) return 'tablet';
  return 'desktop';
}

function getContext(): EventContext {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return {
    browser: detectBrowser(ua),
    os: detectOS(ua),
    deviceType: detectDeviceType(),
    language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

// --- queue / flush -------------------------------------------------------

let queue: TrackingEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function sendEvents(events: TrackingEvent[]): void {
  if (events.length === 0) return;
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // трекинг не должен ломать приложение
  }
}

function flush(): void {
  if (queue.length === 0) return;
  const toSend = queue;
  queue = [];
  sendEvents(toSend);
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_DELAY_MS);
}

// --- public API ----------------------------------------------------------

export function track(
  eventName: string,
  props: Record<string, unknown> = {},
  options: { utm?: UTMParams; context?: EventContext } = {}
): void {
  if (typeof window === 'undefined') return;
  const event: TrackingEvent = {
    originator: ORIGINATOR,
    anonId: getAnonId(),
    sessionId: getSessionId(),
    timestamp: new Date().toISOString(),
    event: eventName,
    props,
  };
  if (currentUserId !== undefined) event.userId = currentUserId;
  if (currentAccountId !== undefined) event.accountId = currentAccountId;
  if (options.utm) event.utm = options.utm;
  if (options.context) event.context = options.context;

  queue.push(event);
  scheduleFlush();
}

export function trackSessionStart(): void {
  if (typeof window === 'undefined') return;
  track('session_start', {}, { context: getContext(), utm: parseUTMParams() });
  requestYMClientId();
}

// Мост с Яндекс.Метрикой: при старте сессии получаем YM ClientID и шлём отдельным
// событием ym_session_start — чтобы связать наш anon_id с данными Метрики по ClientID.
function trackYMSessionStart(clientId: string): void {
  track('ym_session_start', { ym_client_id: clientId });
}

function requestYMClientId(): void {
  if (typeof window === 'undefined') return;
  // Только на боевых доменах eventos (Метрика грузится лишь там).
  if (!window.location.hostname.endsWith('eventos42.ru')) return;

  // Тег Метрики грузится асинхронно — ждём появления window.ym.
  const checkYM = setInterval(() => {
    if (window.ym) {
      clearInterval(checkYM);
      window.ym(YM_COUNTER_ID, 'getClientID', (clientId) => {
        if (clientId) trackYMSessionStart(clientId);
      });
    }
  }, 100);

  // Перестаём ждать через 10 секунд.
  setTimeout(() => clearInterval(checkYM), 10000);
}

export function trackPageView(path: string, referrer?: string): void {
  if (typeof window === 'undefined') return;
  track('page_view', {
    path,
    referrer: referrer || document.referrer || undefined,
  }, { utm: parseUTMParams() });
}

export interface ButtonClickProps {
  button: string;
  location?: string;
  [key: string]: unknown;
}

export function trackButtonClick(props: ButtonClickProps): void {
  track('button_click', props as Record<string, unknown>);
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (flushTimer) clearTimeout(flushTimer);
    flush();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
